/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, ButtonGroup, Avatar } from '@mui/material';
import { FlightLegInfo } from '../../../shared/entities/flightLeg';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { selectFlightLegsInfo, selectSelectedOverlaidFlightLegId } from '../../../store';
import { setSelectedOverlaidFlightLegId } from '../../../store/flightLegs/actions';
import { clearBeamsOnMap, PERFORMANCE_OUTLOOK_MAP_ID } from '../../../store/maps/actions';
import appTheme from '../../common/theme/appTheme.json';
import Scrollbars from '../../common/layout/Scrollbars';

export const LEG_LIST_BUFFER = 52;

const PerformanceOutlookFlightLegList = () => {
  const dispatch = useDispatch();
  const flightLegs: FlightLegInfo[] = useSelector(selectFlightLegsInfo, shallowEqual);
  const selectedOverlaidFlightLegIdPO = useSelector(
    selectSelectedOverlaidFlightLegId(PERFORMANCE_OUTLOOK_MAP_ID),
    shallowEqual,
  );
  const btnGroupRef = useRef<HTMLDivElement>(null);
  const [isGradientPresent, setGradient] = useState<boolean>(false);

  const clickFlightLegTab = useCallback(
    flightLegId => {
      if (flightLegId !== selectedOverlaidFlightLegIdPO) {
        dispatch(clearBeamsOnMap(PERFORMANCE_OUTLOOK_MAP_ID)());
        dispatch(setSelectedOverlaidFlightLegId(PERFORMANCE_OUTLOOK_MAP_ID)(flightLegId));
      }
    },
    [dispatch, selectedOverlaidFlightLegIdPO],
  );

  useEffect(() => {
    if (btnGroupRef && btnGroupRef.current && btnGroupRef.current.offsetWidth + LEG_LIST_BUFFER > window.innerWidth) {
      setGradient(true);
    } else {
      setGradient(false);
    }
  }, [flightLegs]);

  return (
    <React.Fragment>
      <Scrollbars autoHeight>
        <Box
          id="headermenu.flightlegs.group"
          sx={{
            fontFamily: appTheme.viasatFontFamilyThin,
            display: 'flex',
            alignSelf: 'center',
            color: appTheme.primaryTextColor,
            height: '52px',
          }}
        >
          <ButtonGroup ref={btnGroupRef} sx={{ display: 'flex', alignItems: 'center', ml: '4px', pr: '40px' }}>
            {(flightLegs || []).map((flightLeg: FlightLegInfo, index: number) => (
              <Box key={index}>
                <Button
                  onClick={() => {
                    clickFlightLegTab(flightLeg.id);
                  }}
                  variant="text"
                  key={flightLeg.id}
                  sx={{
                    color:
                      flightLeg.id === selectedOverlaidFlightLegIdPO
                        ? appTheme.primaryTextColor
                        : appTheme.mptFormLabelText,
                    height: '48px',
                    pl: '0px',
                    pr: '0px',
                    ml: '12px',
                    mr: '12px',
                    borderBottom: `2px solid ${
                      flightLeg.id === selectedOverlaidFlightLegIdPO
                        ? appTheme.mptLightBlue
                        : appTheme.primaryBackgroundColor
                    }`,
                    borderRadius: '0px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <Avatar
                    sx={{
                      backgroundColor:
                        flightLeg.id === selectedOverlaidFlightLegIdPO
                          ? appTheme.primaryTextColor
                          : appTheme.mptFormLabelText,
                      width: '24px',
                      height: '24px',
                      color: appTheme.mptPrimaryBackgroundColor,
                      fontSize: '12px',
                      mr: '8px',
                    }}
                  >
                    {flightLeg.legOrder}
                  </Avatar>
                  {flightLeg.origin}-{flightLeg.destination}
                </Button>
              </Box>
            ))}
          </ButtonGroup>
        </Box>
      </Scrollbars>
      <Box
        sx={{
          display: isGradientPresent ? 'flex' : 'none',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: 'red',
          height: '52px',
          width: '52px',
          zIndex: 1,
          position: 'absolute',
          right: '0px',
          top: '52px',
          background: `linear-gradient(to right, transparent, ${appTheme.mptPrimaryBackgroundColor} 85%)`,
        }}
      />
    </React.Fragment>
  );
};

export default PerformanceOutlookFlightLegList;
